import '@/styles/globals.css'

import type { FC, ReactElement, ReactNode } from 'react'
import type { AppProps } from 'next/app'
import type { NextPageWithLayout } from '@/types'

import Head from 'next/head'
import { JetBrains_Mono, Plus_Jakarta_Sans } from 'next/font/google'
import { Layout } from '@/components/generals/layout'
import { ThemeProvider } from '@/components/generals/theme-provider'
import { DashboardProvider } from '@/components/dashboard/dashboard-provider'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'

// Plus Jakarta Sans was designed in Indonesia for the city of Jakarta
const sans = Plus_Jakarta_Sans({
    subsets: ['latin']
})

const mono = JetBrains_Mono({
    subsets: ['latin']
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${mono.style.fontFamily};--font-app-heading:${sans.style.fontFamily}}`

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout
}

const withDashboard = (page: ReactElement): ReactNode => <Layout>{page}</Layout>

const App: FC<AppPropsWithLayout> = ({ Component, pageProps }): ReactNode => {
    const getLayout = Component.getLayout ?? withDashboard

    return (
        <div className='isolate h-full font-sans'>
            <Head>
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
                <meta name='viewport' content='width=device-width, initial-scale=1' />
            </Head>

            <ThemeProvider>
                <TooltipProvider delay={250}>
                    <DashboardProvider>
                        {getLayout(<Component {...pageProps} />)}
                        <Toaster />
                    </DashboardProvider>
                </TooltipProvider>
            </ThemeProvider>
        </div>
    )
}

export default App
